import React, { useCallback } from 'react';

import { Radio, VStack } from '@rbilabs/universal-components';
import { Typography } from 'ucl/components/text/Typography';

import ActionButton from 'components/action-button';
import { SORTED_PROD_LOCALES } from 'state/intl/constants';
import { t } from 'state/intl/translations';
import { LANGUAGE_TO_LABEL } from 'state/intl/types';
import { regionEnum } from 'utils/environment';

import {
  ActionsWrapper,
  Disclaimer,
  LanguageSelectionCell,
  LanguageSelectionContainer,
  ModalAdditionalContentWrapper,
} from './styled';

interface ILanguageModalContentsProps {
  disclaimer?: string;
  applyButtonText: string;
  setLanguage: () => void;
  handleSelection: (selectedLanguage: string, mappedRegion: string, isOneStep: boolean) => void;
  regionKey: string;
  langKey: string;
  /**
   * Pass the id of the heading that describes the radio group.
   */
  ariaRadioGroupLabeledBy: string;
}

const LanguageSelectorModalContents: React.FC<React.PropsWithChildren<
  ILanguageModalContentsProps
>> = ({
  regionKey,
  langKey,
  disclaimer,
  applyButtonText,
  setLanguage,
  handleSelection,
  ariaRadioGroupLabeledBy = '',
}) => {
  const regionLanguageSelections = SORTED_PROD_LOCALES.filter(l => l.region === regionEnum).map(
    ({ region: mappedRegion, language: mappedLanguage }) => (
      <LanguageSelectionCell key={`dialog-button-${mappedLanguage}-${mappedRegion}`}>
        <Radio
          value={`${mappedLanguage}-${mappedRegion}`}
          testID={`dialog-button-${mappedLanguage.toLowerCase()}-${mappedRegion.toLowerCase()}`}
          accessibilityLabel={`${t('select')} ${
            LANGUAGE_TO_LABEL[mappedLanguage]
          } / ${mappedRegion}`}
        >
          <VStack ml={2} gap={1}>
            <Typography variant="headerThree" mb="$0" data-lang={mappedLanguage.toLowerCase()}>
              {LANGUAGE_TO_LABEL[mappedLanguage]}
            </Typography>
          </VStack>
        </Radio>
      </LanguageSelectionCell>
    )
  );

  const handleRadioChange = useCallback(
    (value: string) => {
      const [mappedLanguage, mappedRegion] = value.split('-');
      handleSelection(mappedLanguage, mappedRegion, false);
    },
    [handleSelection]
  );

  return (
    <>
      <LanguageSelectionContainer
        name={ariaRadioGroupLabeledBy}
        value={`${langKey}-${regionKey}`}
        onChange={handleRadioChange}
        accessibilityRole="radiogroup"
        aria-labelledby={ariaRadioGroupLabeledBy}
      >
        {regionLanguageSelections}
      </LanguageSelectionContainer>
      {disclaimer && (
        <ModalAdditionalContentWrapper>
          <Disclaimer>{disclaimer}</Disclaimer>
        </ModalAdditionalContentWrapper>
      )}
      <ActionsWrapper>
        <ActionButton onPress={setLanguage} testID="action-button-apply-language-selection">
          {applyButtonText}
        </ActionButton>
      </ActionsWrapper>
    </>
  );
};

export default LanguageSelectorModalContents;
